/* eslint-disable @typescript-eslint/no-explicit-any */
import { LEAD_LABELS_FUNNEL } from '@constants';
import { HeaderRanking, IndicatorsItem } from '@interfaces';

export enum TrendType {
  ConversionRate = 'conversion-rate',
  RespondedChats = 'responded-chats',
  AverageResponseTime = 'average-response-time',
}

export enum TrendTime {
  Daily = 'daily',
  Weekly = 'weekly',
  Monthly = 'monthly',
}

export const TIME_TYPE_OPTIONS = [
  { id: '1', name: 'Este mes' },
  { id: '2', name: 'Último trimestre ' },
  { id: '3', name: 'Último año' },
  { id: 'custom', name: 'Personalizado' },
];

export const TREND_TYPE_OPTIONS: { id: TrendType; name: string }[] = [
  { id: TrendType.ConversionRate, name: 'Tasa de conversión' },
  { id: TrendType.RespondedChats, name: 'Chats respondidos' },
  { id: TrendType.AverageResponseTime, name: 'Tiempo de respuesta promedio' },
];

export const TREND_TIME_OPTIONS = [
  { id: TrendTime.Daily, name: 'Diario' },
  { id: TrendTime.Weekly, name: 'Semanal' },
  { id: TrendTime.Monthly, name: 'Mensual' },
];

export const SALE_RANKING: HeaderRanking[] = [
  { label: 'Vendedor', colSpan: 2 },
  { label: '#', colSpan: 1 },
  { label: 'TC%', colSpan: 1 },
  { label: 'TRP min', colSpan: 1 },
];

export const DISTRICT: HeaderRanking[] = [
  { label: 'Distrito', colSpan: 3 },
  { label: '#', colSpan: 1 },
  { label: 'Leads', colSpan: 1 },
];

export const KPI_KEY = {
  TOTAL_RESPONDED_CHATS: 'totalRespondedChats',
  NEW_RESPONDED_CHATS: 'newRespondedChats',
  CONVERSION_RATE: 'conversionRate',
  AVERAGE_RESPONSE_TIME: 'averageResponseTime',
} as const;

export const KPIS_CONFIG: Record<
  string,
  Pick<IndicatorsItem, 'title' | 'titleTooltip' | 'message' | 'suffix'>
> = {
  [KPI_KEY.TOTAL_RESPONDED_CHATS]: {
    title: 'Chats resp. total',
    titleTooltip: 'Chats respondidos total',
    message:
      'Número de clientes únicos a los que se respondió, sin importar su estado actual.',
  },
  [KPI_KEY.NEW_RESPONDED_CHATS]: {
    title: 'Chats resp. nuevos',
    titleTooltip: 'Chats respondidos nuevos',
    message: 'Número de clientes atendidos dentro de la categoría Asignados.',
  },
  [KPI_KEY.CONVERSION_RATE]: {
    title: 'Tasa de conversión',
    titleTooltip: 'Tasa de conversión',
    message: 'Número de conversiones.',
    suffix: '%',
  },
  [KPI_KEY.AVERAGE_RESPONSE_TIME]: {
    title: 'Tiempo de rpta.',
    titleTooltip: 'Tiempo de respuesta',
    message: 'Tiempo promedio de respuesta en todas las etapas del funnel.',
    suffix: 'min',
  },
};

export const COLUMN_COLORS_FUNNEL = [
  '#0028A9', // Asignado
  '#2855E4', // En atención
  '#078196', // Cotizado
  '#11A77E', // En programación
  '#82CA48', // Pagado
  '#82CA48', // En despacho
  '#82CA48', // Post venta
  '#000000', // Archivados
  '#ABABAB', // Sin atender
];

export const LEAD_COLORS_FUNNEL: Record<any, string> = {
  [LEAD_LABELS_FUNNEL.assigned]: COLUMN_COLORS_FUNNEL[0],
  [LEAD_LABELS_FUNNEL.processing]: COLUMN_COLORS_FUNNEL[1],
  [LEAD_LABELS_FUNNEL.quoted]: COLUMN_COLORS_FUNNEL[2],
  [LEAD_LABELS_FUNNEL.scheduled]: COLUMN_COLORS_FUNNEL[3],
  [LEAD_LABELS_FUNNEL.paid]: COLUMN_COLORS_FUNNEL[4],
  [LEAD_LABELS_FUNNEL['out-for-delivery']]: COLUMN_COLORS_FUNNEL[5],
  [LEAD_LABELS_FUNNEL['after-Sale']]: COLUMN_COLORS_FUNNEL[6],
};

export enum SubstagesName {
  UNASSIGNED = 'Sin asignar',
  UNATTENDING = 'Sin atención',
  UNQUOTED = 'Sin cotizar',
  UNPROGRAMMED = 'Sin programar',
  UNPAID = 'Sin pagar',
  NO_OUT_FOR_DELIVERY = 'Sin despacho',
  NO_AFTER_SALE = 'Sin post venta',
}

export const SUBSTAGES_LABELS: Record<SubstagesName, string> = {
  [SubstagesName.UNASSIGNED]: 'S',
  [SubstagesName.UNATTENDING]: 'S/A',
  [SubstagesName.UNQUOTED]: 'S/C',
  [SubstagesName.UNPROGRAMMED]: 'S/PR',
  [SubstagesName.UNPAID]: 'S/P',
  [SubstagesName.NO_OUT_FOR_DELIVERY]: 'S/D',
  [SubstagesName.NO_AFTER_SALE]: 'S/PV',
};

export const EMOJIS = [
  '😀',
  '😁',
  '😂',
  '🤣',
  '😃',
  '😄',
  '😅',
  '😆',
  '😉',
  '😊',
  '🙂',
  '🙃',
  '😍',
  '😘',
  '😗',
  '😙',
  '😚',
  '😋',
  '😜',
  '😝',
  '😛',
  '🤪',
  '🤨',
  '🧐',
  '😎',
  '🤩',
  '🥳',
  '😏',
  '😒',
  '😞',
  '😔',
  '😟',
  '😕',
  '🙁',
  '☹️',
  '😣',
  '😖',
  '😫',
  '😩',
  '🥺',
  '😢',
  '😭',
  '😤',
  '😠',
  '😡',
  '🤬',
  '🤯',
  '😳',
  '🥵',
  '🥶',
  '😱',
  '😨',
  '😰',
  '😥',
  '😓',
  '🤗',
  '🤔',
  '🤭',
  '🤫',
  '🤥',
  '🤐',
  '🤤',
  '😴',
  '😪',
  '🤢',
  '🤮',
  '🤧',
  '🫠',
  '🤝',
  '🙏',
  '👍',
  '👎',
  '👏',
  '🙌',
  '👐',
  '✋',
  '🤚',
  '🖐',
  '👌',
  '🤌',
  '✌️',
  '🤞',
  '🤟',
  '🤘',
  '🤙',
  '👋',
  '🖖',
  '👌🏻',
  '👍🏻',
  '❤️',
  '🧡',
  '💛',
  '💚',
  '💙',
  '💜',
  '🖤',
  '🤍',
  '🤎',
  '💔',
  '💕',
  '💞',
  '💓',
  '💗',
  '💖',
  '💘',
  '🔥',
  '⭐️',
  '✨',
  '⚡️',
  '💥',
  '💫',
  '💯',
  '✅',
  '❌',
  '⭕️',
  '🔔',
  '🔕',
  '🔒',
  '🔓',
  '🔑',
  '📣',
  '🎉',
  '🎊',
  '🎁',
  '🎈',
  '🍏',
  '🍎',
  '🍐',
  '🍊',
  '🍋',
  '🍌',
  '🍉',
  '🍇',
  '🍓',
  '🍒',
  '🍑',
  '🥭',
  '🍍',
  '🥥',
  '🥝',
  '🍅',
  '🥑',
  '🥦',
  '🍞',
  '🧀',
  '🍗',
  '🍖',
  '🍤',
  '🍜',
  '🍕',
  '🍔',
  '🍟',
  '🍣',
  '🍱',
  '🍛',
  '🍩',
  '🍪',
  '🍰',
  '🍫',
  '🍬',
  '🍭',
  '☕️',
  '🍺',
  '🍷',
  '🥂',
  '🐶',
  '🐱',
  '🐭',
  '🐹',
  '🐰',
  '🦊',
  '🐻',
  '🐼',
  '🐨',
  '🐯',
  '🦁',
  '🐮',
  '🐷',
  '🐸',
  '🐵',
  '🐔',
  '🐧',
  '🐦',
  '🐤',
  '🐣',
  '🦆',
  '🚗',
  '🚕',
  '🚙',
  '🚌',
  '🚎',
  '🏎️',
  '🚑',
  '🚒',
  '🚐',
  '🚚',
  '🚲',
  '🛵',
  '🏍️',
  '✈️',
  '🚀',
  '🚁',
  '⛵️',
  '🚢',
  '🏝️',
  '🏖️',
];
